import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import useClubRosterTransferData from '../../../state/clubRosterTransfer/UseClubRosterTransferData';
import useClubRosterVendorSyncData from '../../../state/clubRosterVendorSync/UseClubRosterVendorSyncData';
import useClubNonAthleteRosterData from '../../../state/clubNonAthleteRoster/UseClubNonAthleteRosterData';

import useGlobalRoutesData from '@usas/ui-web/state/security/UseGlobalRoutesData';
import useBaseUsasClubData from '@usas/ui-web/state/clubBase/UseBaseUsasClubData';
import useOrgGroupTypesData from '@usas/ui-web/state/orgGroupTypes/UseOrgGroupTypesData';
import useGroupFieldNamesData from '@usas/ui-web/state/groupFieldNames/UseGroupFieldNamesData';
import useMemberPersistentStorage from '@usas/ui-web/state/memberPersistentStorage/UseMemberPersistentStorage';

import { DEFAULT_ID } from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';
import { navigateToCrossUiRoute } from '@usas/ui-web/utils/FormatCrossUiRoute';

const INITIAL_STATE = {
  practiceGroupTypeId: DEFAULT_ID,
  siteGroupTypeId: DEFAULT_ID
};

const useClubNonAthleteRoster = () => {
  const navigate = useNavigate();
  const { getGlobalRoute } = useGlobalRoutesData();
  const { setPersistentStorage } = useMemberPersistentStorage();
  const { GROUP_CONSTANTS } = useGroupFieldNamesData();
  const { baseUsasClubState } = useBaseUsasClubData();
  const { orgGroupTypesState, getOrgGroupTypes } = useOrgGroupTypesData();
  const { clubNonAthleteRosterState, searchClubNonAthleteRoster } = useClubNonAthleteRosterData();
  const { clubRosterTransferState, postClubRosterTransfer } = useClubRosterTransferData();
  const { clubRosterVendorSyncState, setClubRosterVendorSyncState, postClubRosterVendorSync
  } = useClubRosterVendorSyncData();
  const [state, setState] = useState(INITIAL_STATE);

  const onEditNonAthlete = (personId) => {
    if (personId && baseUsasClubState.objData?.orgUnitId) {
      const returnUrl = getGlobalRoute('CLUB_NONATHLETE_ROSTER')?.route;
      setPersistentStorage(
        personId,
        import.meta.env.VITE_PROJECT_NAME,
        returnUrl,
        'Back to Club'
      );

      const routeObj = getGlobalRoute('MEMBER_INFO');
      navigateToCrossUiRoute(routeObj?.uIProjectName, routeObj?.route, navigate);
    }
  };

  const onSubmitNonAthleteSync = (nonAthletesToSync) => {
    if (Array.isArray(nonAthletesToSync) && nonAthletesToSync.length > 0) {
      const memberSyncArray = nonAthletesToSync.map((nonAthlete) => ({
        memberId: nonAthlete.memberId,
        orgUnitId: baseUsasClubState.objData.orgUnitId
      }));

      postClubRosterVendorSync(memberSyncArray);
    }
  };

  const onSubmitNonAthleteTransfer = async (nonAthletesToTransfer) => {
    if (Array.isArray(nonAthletesToTransfer) && nonAthletesToTransfer.length > 0) {
      const memberTransferArray = nonAthletesToTransfer.map((nonAthlete) => ({
        personOrgRoleDurationId: nonAthlete.personOrgRoleDurationId,
        transferOrgUnitId: nonAthlete.unClubId,
        transferSignatureDate: formatDate(new Date()),
        transferSignature: '',
        lastCompetitionDate: null
      }));

      await postClubRosterTransfer(memberTransferArray);

      onSubmitNonAthleteFilter(undefined);
    }
  };

  // If formState is undefined, reuse the last filter object
  const onSubmitNonAthleteFilter = (formState = undefined) => {
    if (baseUsasClubState.isObjLoaded === true) {
      searchClubNonAthleteRoster(baseUsasClubState.objData.usasClubId, formState ? createFilterObj(formState) : undefined);

      if (clubRosterVendorSyncState.isSaved === true) {
        setClubRosterVendorSyncState({
          ...clubRosterVendorSyncState,
          isSaving: false,
          isSaved: false,
          objData: {},
          message: '',
        });
      }
    }
  };

  useEffect(() => {
    if (baseUsasClubState.isObjLoaded === true) {
      searchClubNonAthleteRoster(baseUsasClubState.objData.usasClubId, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUsasClubState]);

  useEffect(() => {
    if (orgGroupTypesState.isArrayLoading === false) {
      if (orgGroupTypesState.isArrayLoaded === true) {
        const practiceGroupType = orgGroupTypesState.arrayData.find(
          (x) => x.typeName === GROUP_CONSTANTS.GROUP_TYPE_NAME_PRACTICE_GROUP
        );
        const siteGroupType = orgGroupTypesState.arrayData.find(
          (x) => x.typeName === GROUP_CONSTANTS.GROUP_TYPE_NAME_CLUB_SITE
        );

        setState({
          ...state,
          practiceGroupTypeId: practiceGroupType?.groupTypeId || DEFAULT_ID,
          siteGroupTypeId: siteGroupType?.groupTypeId || DEFAULT_ID,
        });
      } else if (orgGroupTypesState.isArrayLoaded === false) {
        getOrgGroupTypes();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgGroupTypesState]);

  function createFilterObj(formState) {
    return {
      firstName: formState.firstOrPreferredName.trim() !== '' ? formState.firstOrPreferredName.trim() : undefined,
      lastName: formState.lastName.trim() !== '' ? formState.lastName.trim() : undefined,
      practiceOrgGroupId: formState.practiceGroupId > 0 ? formState.practiceGroupId : undefined,
      clubSiteOrgGroupId: formState.siteGroupId > 0 ? formState.siteGroupId : undefined
    };
  }

  return {
    isSaving: clubRosterVendorSyncState.isSaving || clubRosterTransferState.isSaving,
    isLoading: baseUsasClubState.isObjLoading || orgGroupTypesState.isArrayLoading || clubNonAthleteRosterState.isArrayLoading,
    isGridLoading: clubNonAthleteRosterState.isArrayLoading,
    state,
    clubRosterVendorSyncState,
    gridData: clubNonAthleteRosterState.arrayData,
    orgUnitId: baseUsasClubState.objData?.orgUnitId,
    practiceGroupTypeId: state.practiceGroupTypeId,
    siteGroupTypeId: state.siteGroupTypeId,
    onEditNonAthlete,
    onSubmitNonAthleteFilter,
    onSubmitNonAthleteSync,
    onSubmitNonAthleteTransfer
  };
};

export default useClubNonAthleteRoster;
